import _ from "lodash";
import React, { useCallback } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { makeStyles } from "@material-ui/styles";
import { Grid, TextField } from "@material-ui/core";

import CloseIcon from "@material-ui/icons/Close";

import AutocompleteField from "./AutocompleteField";

// Renders the list of filters. setState is a callback that will be called
// with a function that goes from (filters) => newFilters. This is used instead
// of a straightforward onChange callback so we can memoize the filter list.
function FilterList({ library, filters, setState }) {
  const handleFilterChange = useCallback(
    function handleFilterChange(index, filter) {
      setState((filters) => {
        const newList = filters.slice();
        newList[index] = filter;
        return newList;
      });
    },
    [setState]
  );

  const handleFilterDeletion = useCallback(
    function handleFilterDeletion(index) {
      setState((filters) => {
        const newFilters = filters.slice();
        newFilters.splice(index, 1);
        return newFilters;
      });
    },
    [setState]
  );

  const renderedFilters = filters.map((filter, index) => {
    return (
      <Filter
        key={index}
        library={library}
        index={index}
        filter={filter}
        onChange={handleFilterChange}
        onDelete={handleFilterDeletion}
      />
    );
  });

  return <>{renderedFilters}</>;
}

FilterList.propTypes = {
  filters: PropTypes.array,
  setState: PropTypes.func,
};

const filterStyles = makeStyles(() => ({
  filter: {
    marginBottom: 16,
  },
  closeIcon: {
    alignSelf: "center",
    "&:hover": {
      cursor: "pointer",
    },
  },
  bold: {
    fontWeight: 550,
  },
}));

const Filter = React.memo(function Filter({
  library,
  filter,
  index,
  onChange,
  onDelete,
}) {
  const classes = filterStyles();

  function handlePropertyChanges(newProps) {
    const newFilter = _.extend({}, filter, newProps);
    onChange(index, newFilter);
  }

  return (
    <Grid container spacing={1} className={classes.filter}>
      <Grid item xs={3}>
        <TextField
          fullWidth
          label="Property"
          name="prop-name"
          onChange={(e) => handlePropertyChanges({ prop: e.target.value })}
          select
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
          value={filter.prop}
          variant="outlined"
          className={clsx(filter.prop === "" && classes.bold)}
        >
          <option value="" hidden>
            Property
          </option>
          <option value="$browser">Browser</option>
          <option value="$device_id">Device ID</option>
          <option value="$host">Domain</option>
          <option value="$el_attr__href">Href</option>
          <option value="$current_url">Full URL</option>
          <option value="$pathname">Path</option>

          {/* On web, we've replaced $el_text with $el_nested_text which handles
           * many situations better. If they are using web and were not
           * previously using $el_text, we use $el_nested_text. Otherwise use
           * the older $el_text. */}

          {library === "web" && filter.prop !== "$el_text" ? (
            <option value="$el_nested_text">Target Text</option>
          ) : (
            <option value="$el_text">Target Text</option>
          )}
          <option value="$user_id">User ID</option>
        </TextField>
      </Grid>
      <Grid item xs={3}>
        <TextField
          fullWidth
          label="Operator"
          name="op-name"
          onChange={(e) => {
            const value = e.target.value;
            if (value[0] === "!") {
              handlePropertyChanges({ op: value.slice(1), negate: true });
            } else {
              handlePropertyChanges({ op: value, negate: false });
            }
          }}
          select
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
          value={filter.negate ? "!" + filter.op : filter.op}
          variant="outlined"
          className={clsx(filter.op === "" && classes.bold)}
        >
          <option value="" hidden>
            Operator
          </option>
          <option value="contains">Contains</option>
          <option value="wildcard_matches">Matches</option>
          <option value="=">Equals</option>

          <option value="!contains">Does Not Contain</option>
          <option value="!wildcard_matches">Does Not Match</option>
          <option value="!=">Does Not Equal</option>
        </TextField>
      </Grid>
      <Grid item xs={5}>
        <AutocompleteField
          propName={filter.prop}
          fullWidth
          label="Value"
          name="value"
          onChange={(value) => handlePropertyChanges({ val: value })}
          value={filter.val}
          InputLabelProps={{ shrink: true }}
          variant="outlined"
        />
      </Grid>
      <Grid className={clsx(classes.closeIcon)} item xs={1}>
        <CloseIcon onClick={() => onDelete(index)} />
      </Grid>
    </Grid>
  );
});

Filter.propTypes = {
  filter: PropTypes.object,
  index: PropTypes.number,
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
};

export default FilterList;
